import { useEffect, useState } from 'react'
import { Loader } from 'lucide-react'

import type { GetConnectedAccountsByNanoidResponse } from '~/generated/client'
import { useMcpServers } from '../mcp/hooks'
import URLPreview from '../mcp/URLPreview'
import { env } from '~/env'

interface McpTabContentProps {
  connectionInfo: GetConnectedAccountsByNanoidResponse
}

export const McpTabContent = ({ connectionInfo }: McpTabContentProps) => {
  const { data: mcpServers = [], isLoading: isMcpLoading } = useMcpServers({
    authConfigId: connectionInfo?.auth_config?.deprecated?.uuid || undefined,
  })

  const [selectedMcpServerId, setSelectedMcpServerId] = useState<string | null>(
    null,
  )

  // Set default selected MCP server when servers load
  useEffect(() => {
    if (mcpServers && mcpServers.length > 0 && !selectedMcpServerId) {
      setSelectedMcpServerId(mcpServers[0]?.id || null)
    }
  }, [mcpServers, selectedMcpServerId])

  // Get the selected MCP server
  const selectedMcpServer =
    mcpServers?.find((server) => server.id === selectedMcpServerId) ||
    mcpServers?.[0]

  const mcpBaseUrl = env.NEXT_PUBLIC_NODE_ENV === 'development' ? 'http://staging-mcp.composio.dev' : 'https://mcp.composio.dev'

  // Generate MCP URL for the selected server using the correct format
  const mcpUrl = selectedMcpServer?.id
    ? `${mcpBaseUrl}/composio/server/${selectedMcpServer.id}`
    : null

  return (
    <div className="space-y-4">
      {isMcpLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Loader className="mx-auto mb-4 h-8 w-8 animate-spin text-blue-500" />
            <p className="text-gray-600">Loading MCP servers...</p>
          </div>
        </div>
      ) : mcpServers && mcpServers.length >= 1 ? (
        <>
          <div className="rounded-lg border bg-white p-4">
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Select MCP Server
            </label>
            <select
              value={selectedMcpServerId || ''}
              onChange={(e) => setSelectedMcpServerId(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {mcpServers.map((server) => (
                <option key={server.id} value={server.id}>
                  {server.name || `Server ${server.id}`}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              {mcpServers.length} MCP server(s) available for this integration
            </p>
          </div>
          {mcpUrl && <URLPreview mcpUrl={mcpUrl} />}
        </>
      ) : (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="mb-4 text-6xl">🔍</div>
          <h3 className="mb-2 text-lg font-semibold text-gray-900">
            No MCP Servers Found
          </h3>
          <p className="max-w-md text-gray-600">
            No MCP servers are configured for this integration. Create an MCP
            server to enable Model Context Protocol functionality.
          </p>
        </div>
      )}
    </div>
  )
}
